import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, Loader2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyQuestion = (type = 'mcq') => {
  if (type === 'tf') {
    return { type: 'tf', question: '', options: ['True', 'False'], correct_index: 0 };
  }
  if (type === 'short') {
    return { type: 'short', question: '', correct_answer: '', accepted_answers: [] };
  }
  return { type: 'mcq', question: '', options: ['', '', '', ''], correct_index: 0 };
};

/**
 * QuizBuilder — creator-facing UI for building the quiz attached to a lesson.
 *
 * Flow:
 *   1) Optionally click "Generate with AI" — Claude drafts questions from the
 *      lesson title/description; creator can then edit, add, or remove any.
 *   2) Add/edit/remove questions manually. MCQ, True/False, and short-answer
 *      are supported.
 *   3) Save — POSTs to /api/lessons/{id}/quiz. Backend enforces 80% pass.
 *
 * Assumptions:
 *   - Lesson must already be persisted (has a real id, not a client draft) to
 *     attach a quiz. Parent decides when to render this.
 */
export default function QuizBuilder({ lesson, courseTitle, getAuthHeader, onClose, onSaved }) {
  const [existingQuiz, setExistingQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [passingScore, setPassingScore] = useState(80);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/lessons/${lesson.id}/quiz`, { headers: getAuthHeader() });
        setExistingQuiz(res.data);
        setQuestions(res.data.questions || []);
        setPassingScore(res.data.passing_score || 80);
      } catch (_e) {
        // No quiz yet — that's fine.
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lesson.id]);

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/generate-quiz`,
        {
          lesson_title: lesson.title,
          lesson_content: lesson.description || lesson.text_content || courseTitle || '',
          num_questions: 5,
          question_types: ['mcq', 'tf', 'short'],
        },
        { headers: getAuthHeader() }
      );
      setQuestions((prev) => [...prev, ...(res.data?.questions || [])]);
      toast.success(`AI drafted ${res.data?.questions?.length || 0} questions — edit as needed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!questions.length) {
      toast.error('Add at least one question first.');
      return;
    }
    // Basic validation
    for (const [i, q] of questions.entries()) {
      if (!q.question?.trim()) { toast.error(`Question ${i + 1} is empty.`); return; }
      if (q.type !== 'short' && (!q.options || q.options.some((o) => !o.trim()))) {
        toast.error(`Question ${i + 1} has an empty option.`); return;
      }
      if (q.type === 'short' && !q.correct_answer?.trim()) {
        toast.error(`Question ${i + 1} needs a correct answer.`); return;
      }
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/lessons/${lesson.id}/quiz`,
        {
          title: `${lesson.title} Quiz`,
          questions,
          passing_score: Number(passingScore) || 80,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Quiz saved');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (i, patch) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const changeType = (i, type) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? emptyQuestion(type) : q)));

  const updateOption = (i, optIdx, val) =>
    setQuestions((qs) =>
      qs.map((q, idx) => {
        if (idx !== i) return q;
        const opts = [...(q.options || [])];
        opts[optIdx] = val;
        return { ...q, options: opts };
      })
    );

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quiz-builder-modal">
      <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl bg-brand-surface border border-white/10 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-white font-semibold text-lg">Quiz for “{lesson.title}”</h3>
            <p className="text-xs text-gray-400">Students need <span className="text-brand-gold font-semibold">{passingScore}%</span> to unlock the next lesson.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="quiz-builder-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={generateWithAI}
              disabled={generating}
              className="bg-brand-gold text-black hover:brightness-110"
              data-testid="quiz-ai-generate-btn"
            >
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generate with AI</>}
            </Button>
            <Button
              variant="outline"
              onClick={() => setQuestions((qs) => [...qs, emptyQuestion('mcq')])}
              className="border-white/15 text-white"
              data-testid="quiz-add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add question
            </Button>
            <div className="ml-auto flex items-center gap-2 text-sm text-gray-300">
              Pass %:
              <input
                type="number"
                min={0} max={100}
                value={passingScore}
                onChange={(e) => setPassingScore(e.target.value)}
                className="w-16 bg-black/40 border border-white/10 rounded-md px-2 py-1 text-sm text-white text-center"
                data-testid="quiz-passing-score-input"
              />
            </div>
          </div>

          {loading ? (
            <p className="text-gray-400 text-sm">Loading…</p>
          ) : questions.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm border border-dashed border-white/10 rounded-xl">
              No questions yet. Click <span className="text-brand-gold font-semibold">Generate with AI</span> for a first draft, or <span className="text-brand-gold font-semibold">Add question</span> to write them yourself.
            </div>
          ) : (
            questions.map((q, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3" data-testid={`quiz-builder-question-${i}`}>
                <div className="flex items-center gap-2 justify-between">
                  <div className="inline-flex gap-1 rounded-md bg-white/5 p-1">
                    {['mcq', 'tf', 'short'].map((t) => (
                      <button
                        key={t}
                        onClick={() => changeType(i, t)}
                        className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors ${
                          (q.type || 'mcq') === t ? 'bg-brand-gold text-black' : 'text-gray-400 hover:text-white'
                        }`}
                        data-testid={`quiz-type-${i}-${t}`}
                      >
                        {t === 'mcq' ? 'Multi' : t === 'tf' ? 'T/F' : 'Short'}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setQuestions((qs) => qs.filter((_, idx) => idx !== i))}
                    className="text-gray-500 hover:text-red-400"
                    data-testid={`quiz-delete-question-${i}`}
                    aria-label="Delete question"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <input
                  type="text"
                  value={q.question}
                  onChange={(e) => updateQuestion(i, { question: e.target.value })}
                  placeholder="Question…"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-gray-500"
                  data-testid={`quiz-question-text-${i}`}
                />

                {(q.type === 'mcq' || q.type === 'tf' || !q.type) && Array.isArray(q.options) && (
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <div
                        key={oi}
                        className={`flex items-center gap-2 p-2 rounded-md ${
                          q.correct_index === oi ? 'bg-emerald-500/10 border border-emerald-500/40' : 'bg-white/5 border border-transparent'
                        }`}
                      >
                        {/* Click the radio (or the ✔ label) to mark the correct answer.
                            The text input is now a sibling — NOT wrapped in a <label> —
                            so clicking it to type never toggles the radio. */}
                        <button
                          type="button"
                          onClick={() => updateQuestion(i, { correct_index: oi })}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${
                            q.correct_index === oi ? 'border-emerald-400 bg-emerald-400/30' : 'border-white/30 hover:border-white/60'
                          }`}
                          title={q.correct_index === oi ? 'Correct answer' : 'Mark as correct answer'}
                          data-testid={`quiz-correct-${i}-${oi}`}
                          aria-label={`Mark option ${oi + 1} as correct`}
                        >
                          {q.correct_index === oi && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
                        </button>
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(i, oi, e.target.value)}
                          disabled={q.type === 'tf'}
                          className="flex-1 bg-transparent text-sm text-white placeholder:text-gray-500 outline-none disabled:opacity-70"
                          placeholder={`Option ${oi + 1}`}
                          data-testid={`quiz-option-text-${i}-${oi}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {q.type === 'short' && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={q.correct_answer || ''}
                      onChange={(e) => updateQuestion(i, { correct_answer: e.target.value })}
                      placeholder="Correct answer (case-insensitive)"
                      className="w-full bg-emerald-500/10 border border-emerald-500/40 rounded-md px-3 py-2 text-sm text-white"
                      data-testid={`quiz-short-correct-${i}`}
                    />
                    <input
                      type="text"
                      value={(q.accepted_answers || []).join(', ')}
                      onChange={(e) => updateQuestion(i, {
                        accepted_answers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })}
                      placeholder="Also accept (comma-separated alternates)"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-gray-500"
                      data-testid={`quiz-short-accepted-${i}`}
                    />
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10 bg-black/20 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/15 text-white" data-testid="quiz-builder-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-500 text-white hover:bg-emerald-600"
            data-testid="quiz-builder-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save quiz</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
